<template>
  <BottomSheetModal
    v-model="isOpen"
    :auto-height="!showCitySearch"
    :hide-confirm="!showCitySearch"
    :hideHeader="showCitySearch"
    @close="handleClose"
  >
    <template #title>{{ t('filters') }}</template>

    <div class="views-container">
    <div class="main-view" :class="{ 'main-view--hidden': showCitySearch }">
      <!-- City filter -->
      <IosSection form :title="t('filterCity')">
        <IosRow
          :label="t('filterCity')"
          :value="localFilters.city || ''"
          clickable
          first
          :last="!localFilters.city"
          @click="showCitySearch = true"
        />
        <IosRow
          v-if="localFilters.city"
          label=""
          clickable
          last
          :show-chevron="false"
          @click="localFilters.city = ''"
        >
          <template #value>
            <span class="clear-city">{{ t('resetFilters') }}</span>
          </template>
        </IosRow>
      </IosSection>

      <!-- Age group filter -->
      <IosSection form :title="t('filterAgeGroup')">
        <IosRow
          v-for="(option, index) in ageGroupOptions"
          :key="option.value"
          :label="t(`ageGroup_${option.value}`)"
          clickable
          :show-chevron="false"
          :first="index === 0"
          :last="index === ageGroupOptions.length - 1"
          @click="toggleAgeGroup(option.value)"
        >
          <template #value>
            <v-icon
              v-if="localFilters.ageGroup.includes(option.value)"
              size="20"
              color="primary"
            >
              mdi-check
            </v-icon>
          </template>
        </IosRow>
      </IosSection>

      <!-- Visibility filter (auth only) -->
      <IosSection v-if="showAuthSections" form :title="t('filterVisibility')">
        <IosRow
          v-for="(option, index) in visibilityOptions"
          :key="option.value"
          :label="option.label"
          clickable
          :show-chevron="false"
          :first="index === 0"
          :last="index === visibilityOptions.length - 1"
          @click="localFilters.visibility = localFilters.visibility === option.value ? null : option.value"
        >
          <template #value>
            <v-icon
              v-if="localFilters.visibility === option.value"
              size="20"
              color="primary"
            >
              mdi-check
            </v-icon>
          </template>
        </IosRow>
      </IosSection>

      <!-- Recruitment filter -->
      <IosSection form :title="t('filterRecruitment')">
        <IosRow
          v-for="(option, index) in recruitmentOptions"
          :key="option.value"
          :label="option.label"
          clickable
          :show-chevron="false"
          :first="index === 0"
          :last="index === recruitmentOptions.length - 1"
          @click="localFilters.recruitment = localFilters.recruitment === option.value ? null : option.value"
        >
          <template #value>
            <v-icon
              v-if="localFilters.recruitment === option.value"
              size="20"
              color="primary"
            >
              mdi-check
            </v-icon>
          </template>
        </IosRow>
      </IosSection>

      <!-- Role filter (auth only) -->
      <IosSection v-if="showAuthSections" form :title="t('filterRole')">
        <IosRow
          v-for="(option, index) in roleOptions"
          :key="option.value"
          :label="option.label"
          clickable
          :show-chevron="false"
          :first="index === 0"
          :last="index === roleOptions.length - 1"
          @click="toggleRole(option.value)"
        >
          <template #value>
            <v-icon
              v-if="localFilters.roles.includes(option.value)"
              size="20"
              color="primary"
            >
              mdi-check
            </v-icon>
          </template>
        </IosRow>
      </IosSection>

      <!-- Action buttons -->
      <div class="filter-actions">
        <button class="filter-btn filter-btn--reset" @click="resetFilters">
          {{ t('resetFilters') }}
        </button>
        <button class="filter-btn filter-btn--apply" @click="applyFilters">
          {{ t('applyFilters') }}
        </button>
      </div>
    </div>

    <!-- City search subview (matches TeamFormModal) -->
    <Transition name="slide">
      <div v-if="showCitySearch" class="subview">
        <div class="subview-header">
          <button class="back-btn" @click="showCitySearch = false; citySearchQuery = ''; cityResults = []">
            <v-icon size="22">mdi-chevron-left</v-icon>
          </button>
          <div class="subview-title">{{ t('filterCity') }}</div>
          <div class="spacer"></div>
        </div>

        <div class="subview-scroll">
          <IosSection form>
            <div class="search-input-wrapper-ios">
              <v-icon size="20" class="search-icon">mdi-magnify</v-icon>
              <input
                v-model="citySearchQuery"
                type="text"
                class="search-input"
                :placeholder="t('filterCityPlaceholder')"
                @input="onCitySearch"
              />
              <v-progress-circular v-if="searchingCities" indeterminate size="18" width="2" class="ml-2" />
            </div>
          </IosSection>

          <IosSection form v-if="cityResults.length > 0">
            <IosRow
              v-for="(city, index) in cityResults"
              :key="city.id"
              :label="city.label"
              :subtitle="city.subtitle"
              :first="index === 0"
              :last="index === cityResults.length - 1"
              clickable
              @click="selectCity(city)"
            />
          </IosSection>

          <div v-else-if="!cityResults.length && citySearchQuery.length >= 2 && !searchingCities" class="search-empty">
            {{ t('noResults') }}
          </div>
        </div>
      </div>
    </Transition>
    </div>
  </BottomSheetModal>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import BottomSheetModal from '@/components/BottomSheetModal.vue';
import IosSection from '@/components/ios/IosSection.vue';
import IosRow from '@/components/ios/IosRow.vue';

const { t } = useI18n();

export interface TeamFilters {
  city: string;
  ageGroup: string[];
  visibility: string | null; // 'public' | 'private' | null
  recruitment: string | null; // 'true' | 'false' | null
  roles: string[]; // 'admins' | 'players' | 'coaches' | 'externalOrganizers'
}

const props = withDefaults(defineProps<{
  modelValue: boolean;
  filters: TeamFilters;
  showAuthSections?: boolean;
}>(), {
  showAuthSections: true,
});

const emit = defineEmits<{
  (e: 'update:modelValue', val: boolean): void;
  (e: 'apply', filters: TeamFilters): void;
}>();

const isOpen = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val),
});

const localFilters = ref<TeamFilters>({ ...props.filters, ageGroup: [...props.filters.ageGroup], roles: [...props.filters.roles] });

// Sync when opening
watch(() => props.modelValue, (val) => {
  if (val) {
    localFilters.value = { ...props.filters, ageGroup: [...props.filters.ageGroup], roles: [...props.filters.roles] };
  }
});

const ageGroupOptions = [
  { value: 'preschool' },
  { value: 'youngerJuniors' },
  { value: 'olderJuniors' },
  { value: 'women' },
  { value: 'youth' },
  { value: 'juniors' },
  { value: 'seniorsUnregistered' },
  { value: 'seniors' },
];

const visibilityOptions = computed(() => [
  { value: 'public', label: t('publicTeams') },
  { value: 'private', label: t('privateTeams') },
]);

const recruitmentOptions = computed(() => [
  { value: 'true', label: t('recruitingTeams') },
  { value: 'false', label: t('notRecruitingTeams') },
]);

const roleOptions = computed(() => [
  { value: 'admins', label: t('filterRoleAdmin') },
  { value: 'players', label: t('filterRolePlayer') },
  { value: 'coaches', label: t('filterRoleCoach') },
  { value: 'externalOrganizers', label: t('filterRoleExternal') },
]);

function toggleRole(value: string) {
  const idx = localFilters.value.roles.indexOf(value);
  if (idx >= 0) {
    localFilters.value.roles.splice(idx, 1);
  } else {
    localFilters.value.roles.push(value);
  }
}

function toggleAgeGroup(value: string) {
  const idx = localFilters.value.ageGroup.indexOf(value);
  if (idx >= 0) {
    localFilters.value.ageGroup.splice(idx, 1);
  } else {
    localFilters.value.ageGroup.push(value);
  }
}

function resetFilters() {
  localFilters.value = {
    city: '',
    ageGroup: [],
    visibility: null,
    recruitment: null,
    roles: [],
  };
  emit('apply', { ...localFilters.value, ageGroup: [], roles: [] });
  isOpen.value = false;
}

function applyFilters() {
  emit('apply', { ...localFilters.value, ageGroup: [...localFilters.value.ageGroup], roles: [...localFilters.value.roles] });
  isOpen.value = false;
}

function handleClose() {
  showCitySearch.value = false;
  citySearchQuery.value = '';
  cityResults.value = [];
  isOpen.value = false;
}

// City search
interface CityOption {
  id: number;
  label: string;
  subtitle?: string;
}

const showCitySearch = ref(false);
const citySearchQuery = ref('');
const cityResults = ref<CityOption[]>([]);
const searchingCities = ref(false);
let citySearchTimeout: ReturnType<typeof setTimeout> | null = null;

function onCitySearch() {
  if (citySearchTimeout) clearTimeout(citySearchTimeout);
  citySearchTimeout = setTimeout(() => {
    if (!citySearchQuery.value || citySearchQuery.value.length < 2) {
      cityResults.value = [];
      return;
    }
    searchCities(citySearchQuery.value.trim());
  }, 300);
}

async function searchCities(query: string) {
  searchingCities.value = true;
  try {
    const url = new URL('https://nominatim.openstreetmap.org/search');
    url.searchParams.set('q', query);
    url.searchParams.set('format', 'jsonv2');
    url.searchParams.set('addressdetails', '1');
    url.searchParams.set('limit', '10');
    url.searchParams.set('featuretype', 'city');

    const res = await fetch(url.toString(), {
      headers: { 'Accept': 'application/json' },
    });
    const data = await res.json();

    const mapped: CityOption[] = (Array.isArray(data) ? data : []).map((r: any) => {
      const addr = r.address || {};
      const cityLike = addr.city || addr.town || addr.village || r.display_name?.split(',')?.[0] || r.name;
      const region = addr.state || addr.region || addr.county;
      const country = addr.country || 'Česko';
      return {
        id: Number(r.place_id),
        label: String(cityLike),
        subtitle: [region, country].filter(Boolean).join(', '),
      };
    });

    // Remove duplicates
    const seen = new Set<string>();
    cityResults.value = mapped.filter(m => {
      const key = m.label.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  } catch (e) {
    console.error('City search failed:', e);
    cityResults.value = [];
  } finally {
    searchingCities.value = false;
  }
}

function selectCity(city: CityOption) {
  localFilters.value.city = city.label;
  showCitySearch.value = false;
  citySearchQuery.value = '';
  cityResults.value = [];
}
</script>

<style lang="scss" scoped>
.views-container {
  position: relative;
  overflow: hidden;
}

.main-view {
  transition: transform 0.35s cubic-bezier(0.32, 0.72, 0, 1), 
              opacity 0.25s ease;
  padding-bottom: 20px;
  
  &--hidden {
    opacity: 0.3;
    transform: translateX(-100px) scale(0.92);
    pointer-events: none;
  }
}

.filter-actions {
  display: flex;
  gap: 12px;
  padding: 16px 16px 0;
}

.filter-btn {
  flex: 1;
  padding: 12px 16px;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: opacity 0.2s;

  &:active {
    opacity: 0.7;
  }

  &--reset {
    background: rgba(128, 128, 128, 0.15);
    color: var(--v-theme-on-surface, #333);
  }

  &--apply {
    background: rgb(var(--v-theme-primary));
    color: white;
  }
}

.clear-city {
  color: rgb(var(--v-theme-primary));
  font-size: 14px;
  font-weight: 500;
}

// Slide transition - iOS style
.slide-enter-active {
  transition: transform 0.35s cubic-bezier(0.32, 0.72, 0, 1);
}

.slide-leave-active {
  transition: transform 0.3s cubic-bezier(0.32, 0.72, 0, 1);
}

.slide-enter-from {
  transform: translateX(100%) !important;
}

.slide-leave-to {
  transform: translateX(100%) !important;
}

.subview {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  margin: 0 auto;
  width: 100%;
  max-width: 600px;
  height: 92vh;
  display: flex;
  flex-direction: column;
  background: rgb(var(--v-theme-sheetBackground));
  border-radius: 32px 32px 0 0;
  z-index: 10000;
  overflow: hidden;
  transform: translateX(0);
}

.subview-header {
  position: absolute;
  top: 8px;
  left: 8px;
  right: 8px;
  display: flex;
  align-items: center;
  z-index: 10;
  pointer-events: none;

  > * {
    pointer-events: auto;
  }
}

.subview-scroll {
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 20px;
  padding-top: 70px;
}

.back-btn {
  width: 45px;
  height: 45px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: none;
  background: rgb(var(--v-theme-sheetCloseBtn));
  cursor: pointer;
  transition: all 0.2s ease;

  :deep(.v-icon) {
    color: rgb(var(--v-theme-sheetText)) !important;
  }

  &:hover {
    filter: brightness(0.92);
  }

  &:active {
    transform: scale(0.95);
  }
}

.subview-title {
  flex: 1;
  text-align: center;
  font-size: 1.1rem;
  font-weight: 600;
  color: rgb(var(--v-theme-sheetText));
}

.spacer {
  width: 45px;
}

.search-input-wrapper-ios {
  display: flex;
  align-items: center;
  padding: 0 16px;
  min-height: 48px;

  .search-icon {
    color: rgba(var(--v-theme-sheetText), 0.5);
    margin-right: 12px;
  }

  .search-input {
    flex: 1;
    background: transparent;
    border: none;
    outline: none;
    font-size: 1rem;
    color: rgb(var(--v-theme-sheetText));

    &::placeholder {
      color: rgba(var(--v-theme-sheetText), 0.4);
    }
  }
}

.search-empty {
  padding: 24px;
  text-align: center;
  color: rgba(var(--v-theme-sheetText), 0.5);
}
</style>
